import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { 
  CheckCircleIcon, 
  TruckIcon, 
  GlobeAmericasIcon, 
  ShieldCheckIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { SUBMIT_CONTACT } from '../services/graphql/mutations/contact.js';

const Landing = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  const [submitContact, { loading: submittingContact }] = useMutation(SUBMIT_CONTACT);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data } = await submitContact({
        variables: {
          input: contactForm
        }
      });

      if (data.submitContact.success) {
        // Show success notification
        setNotificationMessage('¡Gracias por contactarnos! Su consulta ha sido enviada exitosamente. Le responderemos dentro de las próximas 24 horas.');
        setNotificationType('success');
        setShowNotification(true);
        
        // Reset form
        setContactForm({
          name: '',
          email: '',
          message: ''
        });
        
        // Hide notification after 8 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 8000);
      } else {
        throw new Error(data.submitContact.message || 'Error al enviar el mensaje');
      }
    } catch (error) {
      console.error('Contact submission error:', error);
      setNotificationMessage('Error al enviar su consulta. Por favor intente nuevamente o contáctenos directamente.');
      setNotificationType('error');
      setShowNotification(true);
      
      // Hide notification after 6 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 6000);
    }
  };

  const features = [
    {
      icon: TruckIcon,
      title: 'Seguimiento en Tiempo Real',
      description: 'Monitoree sus envíos desde el origen hasta el destino con actualizaciones en tiempo real.'
    },
    {
      icon: GlobeAmericasIcon,
      title: 'Cobertura Internacional',
      description: 'Servicios de logística y seguimiento para envíos nacionales e internacionales.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Seguridad Garantizada',
      description: 'Sus envíos están protegidos con la más alta seguridad y sistemas de seguimiento avanzados.'
    }
  ];

  return (
    <div className="min-h-screen bg-cover bg-center" style={{ backgroundImage: "url('/LandingBackgroundSmall.jpg')"}}>
      {/* Notification Modal */}
      {showNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in">
          <div className="bg-white rounded-lg shadow-soft-xl p-6 m-4 max-w-md w-full animate-scale-in">
            <div className="flex items-center">
              <div className={`flex-shrink-0 ${notificationType === 'success' ? 'text-primary-600' : 'text-error-600'}`}>
                {notificationType === 'success' ? (
                  <CheckCircleIcon className="h-6 w-6" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {notificationMessage}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowNotification(false)}
                className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header>
        <div className="mx-auto px-4 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center">
              <div className="flex-shrink-0">
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-5xl font-bold text-gray-700 mb-6">
              Sistema ACTrack
            </h1>
          </div>
        </div>
      </section>


      {/* Contact Section */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Contact Info */}
            <div>
                <div className="flex flex-col sm:flex-row gap-6 py-1 justify-center">
                  <img 
                    src="/ACTrackLogo.png" 
                    alt="ACTrack - American Cargo" 
                    className="h-80 w-80"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-6 py-1 justify-center">
                  <Link
                    to="/register"
                    className="bg-primary-600 text-white hover:bg-primary-700 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:transform hover:scale-105 shadow-soft-lg"
                  >
                    Regístrese Ahora
                    <ArrowRightIcon className="inline-block ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="bg-white text-primary-600 hover:bg-primary-50 border-2 border-primary-600 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:transform hover:scale-105 shadow-soft-lg"
                  >
                    Accede - Inicia Sesión
                  </Link>
                </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-soft-lg p-4">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Envíenos un mensaje
              </h3>              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Ingrese su nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="su.email@ejemplo.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Escriba su consulta o mensaje..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submittingContact}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                    submittingContact
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 hover:transform hover:scale-105'
                  } text-white shadow-soft-lg`}
                >
                  {submittingContact ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
              </form>
              
              <p className="text-sm text-gray-500 mt-4">
                * Campos requeridos. Sus datos están protegidos, solo se usarán para su consulta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">ACTrack</h3>
              <p className="text-gray-400">
                Sistema de seguimiento de envíos de American Cargo. 
                Confiabilidad y transparencia en cada entrega.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces Rápidos</h4>
              <ul className="space-y-1">
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Iniciar Sesión
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Registrarse
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <p className="text-gray-400 mb-2">status@americancargocr.com</p>
              <p className="text-gray-400">Lunes a Viernes, 8:00 AM - 5:00 PM</p>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-4 pt-4 text-center">
            <p className="text-gray-400">
              © 2024 American Cargo. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;